import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { Loader2, Save, GripVertical, X, Plus } from 'lucide-react'
import { z } from 'zod'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useLanguage } from '@/context/LanguageContext'
import { reorderFieldArray } from '@/components/shared/DraggableFieldArray'
import { cn } from '@/lib/utils'
import {
  GET_FILTER_SETTINGS,
  GET_ATTRIBUTE_GROUPS_FOR_FILTER_PICKER,
  UPDATE_FILTER_SETTINGS,
} from './filterSettingsQueries'

const filterSettingsFormSchema = z.object({
  filter_panel_group_ids: z.array(z.number()),
})

type FilterSettingsFormValues = z.infer<typeof filterSettingsFormSchema>

interface AttributeGroupProfile {
  name: string
  language: string
}

interface AttributeGroupForPicker {
  id: number
  code: string
  product_attribute_group_profiles: AttributeGroupProfile[]
  product_attributes: { id: number; product_containers: { id: number }[] }[]
}

// Flattens the raw `[{ type, id }, ...]` config down to the ids this admin
// UI understands (`attribute_group` only), dropping anything malformed and
// deduping while preserving first occurrence -- a duplicate id would break
// react-beautiful-dnd's `draggableId` uniqueness and would also trip the
// backend's own duplicate check on save.
function flattenFilterPanelSections(raw: unknown): number[] {
  if (!Array.isArray(raw)) return []
  const seen = new Set<number>()
  const ids: number[] = []
  for (const entry of raw) {
    const id = entry?.id
    if (
      entry?.type === 'attribute_group' &&
      Number.isInteger(id) &&
      id > 0 &&
      !seen.has(id)
    ) {
      seen.add(id)
      ids.push(id)
    }
  }
  return ids
}

// Distinct product count across every attribute in the group -- used to flag
// groups that are all-zero (most likely a dead PIM twin, see
// `filterSettingsQueries.ts`'s doc comment on the picker query).
function getGroupUsageCount(group: AttributeGroupForPicker): number {
  const productIds = new Set<number>()
  for (const attribute of group.product_attributes ?? []) {
    for (const container of attribute.product_containers ?? []) {
      productIds.add(container.id)
    }
  }
  return productIds.size
}

export default function FilterSettings() {
  const { language } = useLanguage()

  const { data, loading, error } = useQuery(GET_FILTER_SETTINGS)
  const {
    data: groupsData,
    loading: groupsLoading,
    error: groupsError,
  } = useQuery(GET_ATTRIBUTE_GROUPS_FOR_FILTER_PICKER)

  // No `onError` here on purpose: with `onError` set, Apollo 3 resolves the
  // mutate promise instead of rejecting it, so a `try/catch` around it never
  // sees the failure and the generic toast fires alongside (or instead of)
  // the backend's actual validation message. Errors are handled entirely in
  // `onSubmit`'s `catch` below.
  const [updateFilterSettings, { loading: saving }] = useMutation(
    UPDATE_FILTER_SETTINGS
  )

  const form = useForm<FilterSettingsFormValues>({
    resolver: zodResolver(filterSettingsFormSchema),
    mode: 'onSubmit',
    defaultValues: { filter_panel_group_ids: [] },
  })

  const shopSetting = data?.findFirstShopSetting
  // Query succeeded but returned nothing (no `ShopSetting` row yet) or failed
  // outright — either way there's no `id` to save against, so the form must
  // not look editable/saveable. See the banner + disabled wrapper below and the
  // `onSubmit` guard.
  const settingsLoadFailed = !loading && !shopSetting
  // The picker itself failed to load -- fold this into the same disabled
  // state rather than rendering every configured id as "Deleted", which
  // would be wrong (we simply don't know).
  const groupsLoadFailed = !groupsLoading && !!groupsError
  const loadFailed = settingsLoadFailed || groupsLoadFailed

  // Reset is keyed ONLY on the settings query, not the groups query -- the
  // groups list can refetch independently (e.g. a background refetch) without
  // ever wiping an admin's in-progress reorder/selection.
  useEffect(() => {
    if (loading) return

    form.reset({
      filter_panel_group_ids: flattenFilterPanelSections(
        shopSetting?.filter_panel_sections
      ),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data])

  const groups: AttributeGroupForPicker[] =
    groupsData?.findManyProductAttributeGroup ?? []
  const groupsById = new Map(groups.map(group => [group.id, group]))

  const getGroupLabel = (group: AttributeGroupForPicker) =>
    group.product_attribute_group_profiles.find(
      p => p.language.toLowerCase() === language.toLowerCase()
    )?.name ||
    group.product_attribute_group_profiles[0]?.name ||
    'Unknown'

  const selectedIds = form.watch('filter_panel_group_ids') || []
  const selectedIdSet = new Set(selectedIds)

  // Any selected id missing from a successfully-loaded groups list is a
  // stale/deleted group. Rendered as "Deleted group #<id>" rather than
  // silently dropped (a save would otherwise quietly rewrite the admin's
  // config), but it also blocks Save -- the backend rejects unknown ids
  // outright, so the admin must explicitly remove it first.
  const hasDeletedGroups =
    !groupsLoading &&
    !groupsError &&
    selectedIds.some(id => !groupsById.has(id))

  const availableGroups = groups
    .filter(group => !selectedIdSet.has(group.id))
    // Zero-usage groups (most likely a dead PIM twin) sort last rather than
    // being hidden -- an admin may still deliberately want an empty group.
    .sort((a, b) => {
      const aZero = getGroupUsageCount(a) === 0
      const bZero = getGroupUsageCount(b) === 0
      if (aZero === bZero) return 0
      return aZero ? 1 : -1
    })

  const handleAddGroup = (groupId: number) => {
    const current = form.getValues('filter_panel_group_ids') || []
    form.setValue('filter_panel_group_ids', [...current, groupId], {
      shouldDirty: true,
    })
  }

  const handleRemoveGroup = (groupId: number) => {
    const current = form.getValues('filter_panel_group_ids') || []
    form.setValue(
      'filter_panel_group_ids',
      current.filter(id => id !== groupId),
      { shouldDirty: true }
    )
  }

  const handleDragEnd = (result: any) => {
    if (!result.destination) return
    const current = form.getValues('filter_panel_group_ids') || []
    form.setValue(
      'filter_panel_group_ids',
      reorderFieldArray(current, result.source.index, result.destination.index),
      { shouldDirty: true }
    )
  }

  const onSubmit = async (values: FilterSettingsFormValues) => {
    if (!shopSetting) {
      toast.error(
        'Filter settings could not be loaded, so there is nothing to save. Reload the page and try again.'
      )
      return
    }

    if (hasDeletedGroups) {
      toast.error(
        'Remove deleted groups from the selected list before saving.'
      )
      return
    }

    try {
      // `shopSettingsId` must always be sent — the resolver silently no-ops
      // without it — and `filter_panel_sections` must always be sent as a
      // bare array (full-replace semantics, `[]` clears it back to "show all
      // groups"), even when empty. See `filterSettingsQueries.ts`'s doc
      // comment. Re-inflate the picked ids into the typed entries the
      // backend expects; never persist anything else client-side.
      await updateFilterSettings({
        variables: {
          shopSettingsId: shopSetting.id,
          filter_panel_sections: values.filter_panel_group_ids.map(id => ({
            type: 'attribute_group',
            id,
          })),
        },
      })
      toast.success('Filter settings saved')
    } catch (saveError: any) {
      console.error('Save filter settings error:', saveError)
      toast.error(
        saveError?.message ?? 'Failed to save filter settings'
      )
    }
  }

  if (loading || groupsLoading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex justify-between items-center mb-8 max-w-[1000px]">
            <HeaderSection
              title="Filters"
              subtitle="Choose which attribute groups appear as sections in the B2B portal Filters panel, and in what order"
            />
            <Button
              type="submit"
              disabled={saving || loadFailed || hasDeletedGroups}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>

          {loadFailed && (
            <div className="max-w-[1000px] mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {settingsLoadFailed &&
                (error
                  ? `Failed to load filter settings: ${error.message}`
                  : 'Filter settings are unavailable right now, so changes here cannot be saved.')}
              {settingsLoadFailed && groupsLoadFailed && <br />}
              {groupsLoadFailed &&
                `Failed to load attribute groups: ${groupsError?.message}`}
            </div>
          )}

          {!loadFailed && hasDeletedGroups && (
            <div className="max-w-[1000px] mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              One or more selected groups no longer exist. Remove them from
              the selected list below before saving.
            </div>
          )}

          <div className="max-w-[1000px] space-y-6">
            <div
              className={cn(loadFailed && 'pointer-events-none opacity-60')}
              aria-disabled={loadFailed}
            >
              <div className="mb-4 space-y-1">
                <Label className="text-sm font-medium">
                  Filter panel sections
                </Label>
                <p className="text-sm text-muted-foreground">
                  Leave empty to show <strong>all</strong> attribute groups
                  in the default order. Saving zero groups does{' '}
                  <strong>not</strong> hide the Filters panel.
                </p>
                <p className="text-sm text-muted-foreground">
                  Groups whose options have no matching products in a
                  category are hidden automatically for that category.
                </p>
              </div>

              <DragDropContext onDragEnd={handleDragEnd}>
                <div className="space-y-6">
                  <div>
                    <Label className="text-sm font-medium">Selected</Label>
                    {selectedIds.length === 0 ? (
                      <p className="text-sm text-muted-foreground mt-2">
                        No groups selected — <strong>all</strong> attribute
                        groups will show, in the default order.
                      </p>
                    ) : (
                      <Droppable
                        droppableId="selected-filter-groups"
                        direction="vertical"
                      >
                        {provided => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="mt-2 space-y-2"
                          >
                            {selectedIds.map((groupId, index) => {
                              const group = groupsById.get(groupId)
                              const isDeleted = !groupsError && !group
                              const usageCount = group
                                ? getGroupUsageCount(group)
                                : null

                              return (
                                <Draggable
                                  key={groupId}
                                  draggableId={String(groupId)}
                                  index={index}
                                >
                                  {dragProvided => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      className={cn(
                                        'flex items-center gap-2 rounded-md border bg-white px-3 py-2',
                                        isDeleted &&
                                          'border-destructive/50 bg-destructive/5'
                                      )}
                                    >
                                      <div
                                        {...dragProvided.dragHandleProps}
                                        className="cursor-grab text-muted-foreground shrink-0"
                                      >
                                        <GripVertical className="h-4 w-4" />
                                      </div>
                                      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                                        {group ? (
                                          <>
                                            <span className="text-sm font-medium truncate">
                                              {getGroupLabel(group)}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                              {group.code}
                                            </span>
                                            <span className="text-xs text-muted-foreground">
                                              #{group.id}
                                            </span>
                                            {usageCount === 0 && (
                                              <Badge
                                                variant="destructive"
                                                className="text-[10px]"
                                              >
                                                No products
                                              </Badge>
                                            )}
                                          </>
                                        ) : isDeleted ? (
                                          <span className="text-sm text-destructive">
                                            Deleted group #{groupId}
                                          </span>
                                        ) : (
                                          <span className="text-sm text-muted-foreground">
                                            Group #{groupId} (details
                                            unavailable)
                                          </span>
                                        )}
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 shrink-0 text-red-500 hover:text-red-700"
                                        onClick={() =>
                                          handleRemoveGroup(groupId)
                                        }
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Available</Label>
                    {groupsError ? (
                      <p className="text-sm text-muted-foreground mt-2">
                        Attribute groups could not be loaded.
                      </p>
                    ) : availableGroups.length === 0 ? (
                      <p className="text-sm text-muted-foreground mt-2">
                        All attribute groups are selected.
                      </p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {availableGroups.map(group => {
                          const usageCount = getGroupUsageCount(group)
                          return (
                            <button
                              type="button"
                              key={group.id}
                              onClick={() => handleAddGroup(group.id)}
                              className="w-full flex items-center gap-2 rounded-md border px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                            >
                              <Plus className="h-4 w-4 text-green-500 shrink-0" />
                              <span className="text-sm font-medium truncate">
                                {getGroupLabel(group)}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {group.code}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                #{group.id}
                              </span>
                              {usageCount === 0 && (
                                <Badge
                                  variant="secondary"
                                  className="text-[10px] ml-auto"
                                >
                                  No products
                                </Badge>
                              )}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </DragDropContext>
            </div>
          </div>
        </form>
      </Form>
    </PageContent>
  )
}
